<template>
  <nav class="sf-pagination">
    <div class="sf-pagination__item prev">
      <BKButton
        :class="{
          'sf-arrow--transparent': !canGoPrev
        }"
        :disabled="!canGoPrev"
        :aria-label="'Go to previous page'"
        variant="secondary"
        data-testid="pagination-button-prev"
        @click="go(getPrev)"
      >
        <template #label>
          <SfIcon
            :icon="chevronLeft"
            width="24"
            height="24"
          />
          <span v-if="!isMobile">{{ $t('Back') }}</span>
        </template>
      </BKButton>
    </div>

    <SfButton
      class="sf-pagination__item sf-pagination__item-number sf-button--pure"
      :class="{ 'display-none': !showFirst }"
      @click="go(1)"
    >
      <template #label>
        1
      </template>
    </SfButton>
    <div
      :class="{ 'display-none': firstVisiblePageNumber <= 2 }"
      class="sf-pagination__item sf-pagination__item-number"
    >
      ...
    </div>

    <div
      v-for="page in limitedPageNumbers"
      :key="page"
    >
      <SfButton
        :key="page"
        class="sf-pagination__item sf-pagination__item-number"
        :class="{
          'sf-button--pure': currentPage !== page,
          current: currentPage === page
        }"
        @click="currentPage === page ? null : go(page)"
      >
        {{ page }}
      </SfButton>
    </div>
    <div
      v-if="showLast"
      :class="{
        'display-none': lastVisiblePageNumber >= total - 1
      }"
      class="sf-pagination__item sf-pagination__item-number"
    >
      ...
    </div>

    <!-- TODO Thomas: not sure what this is exactly and if it's needed -->
    <!-- <SfButton
      class="sf-pagination__item sf-button--pure"
      @click="go(total)"
    >
      <template #label>
        {{ total }}
      </template>
    </SfButton> -->

    <div class="sf-pagination__item next">
      <BKButton
        :class="{
          'sf-arrow--transparent': !canGoNext
        }"
        :disabled="!canGoNext"
        :aria-label="'Go to next page'"
        variant="secondary"
        data-testid="pagination-button-next"
        @click="go(getNext)"
      >
        <template #label>
          <span v-if="!isMobile">{{ $t('Forward') }}</span>
          <SfIcon
            :icon="chevronRight"
            width="24"
            height="24"
          />
        </template>
      </BKButton>
    </div>
  </nav>
</template>

// eslint-disable-next-line vue/block-lang
<script>
import { defineComponent } from '@nuxtjs/composition-api';
import { SfButton, SfIcon } from '@storefront-ui/vue';
import { isBreakpointLg } from '~/cms/helpers/breakpoints';
import BKButton from '~/components/BK/BKButton.vue';
import { chevronLeft, chevronRight } from '~/components/BK/icons';

export default defineComponent({
  name: 'CategoryPagination',
  components: {
    SfIcon,
    BKButton,
    SfButton,
  },
  props: {
    total: {
      type: Number,
      default: 0,
    },
    visible: {
      type: Number,
      default: 5,
    },
    hasArrows: {
      type: Boolean,
      default: true,
    },
    current: {
      type: Number,
      default: 1,
    },
    pageParamName: {
      type: String,
      default: 'page',
    },
  },
  setup() {
    const isMobile = false;
    return {
      chevronLeft,
      chevronRight,
      isMobile,
    };
  },
  computed: {
    hasRouter() {
      return false;
    },
    currentPage() {
      return this.current;
    },
    getPrev() {
      return this.currentPage === this.firstVisiblePageNumber ? this.currentPage : this.currentPage - 1;
    },
    canGoPrev() {
      return this.currentPage !== this.firstVisiblePageNumber;
    },
    getNext() {
      return this.currentPage === this.lastVisiblePageNumber ? this.currentPage : this.currentPage + 1;
    },
    canGoNext() {
      return this.currentPage !== this.lastVisiblePageNumber;
    },
    showFirst() {
      return this.firstVisiblePageNumber > 1;
    },
    showLast() {
      return this.lastVisiblePageNumber < this.total;
    },
    listOfPageNumbers() {
      return [...new Array(this.total)].map((_, i) => i + 1);
    },
    limitedPageNumbers() {
      if (this.total <= this.visible) {
        return this.listOfPageNumbers;
      }
      if (this.currentPage < this.visible - Math.floor(this.visible / 2) + 1) {
        return this.listOfPageNumbers.slice(0, this.visible);
      }
      if (this.total - this.currentPage < this.visible - Math.ceil(this.visible / 2) + 1) {
        return this.listOfPageNumbers.slice(this.total - this.visible);
      }
      return this.listOfPageNumbers.slice(this.currentPage - Math.ceil(this.visible / 2), this.currentPage + Math.floor(this.visible / 2));
    },
    firstVisiblePageNumber() {
      return this.limitedPageNumbers[0];
    },
    lastVisiblePageNumber() {
      return this.limitedPageNumbers[this.limitedPageNumbers.length - 1];
    },
  },
  mounted() {
    window.addEventListener('resize', this.calculateBreakpoints);
    this.calculateBreakpoints();
  },
  unmounted() {
    window.removeEventListener('resize', this.calculateBreakpoints);
  },
  methods: {
    go(page) {
      this.$emit('click', page);
    },
    getLinkTo(page) {
      const pageNumber = page.toString();
      return pageNumber;
    },
    calculateBreakpoints() {
      this.isMobile = isBreakpointLg(window);
    },
  },
});
</script>
<style lang="scss">
@import '~@storefront-ui/shared/styles/components/molecules/SfPagination.scss';

.sf-pagination {
  .prev,
  .next {
    --pagination-item-width: rem(137) !important;
    --pagination-item-height: rem(48) !important;

    @include for-desktop {
      margin: 0 24px;
    }
  }

  &__item {
    font-size: 16px;
    line-height: 150%;
    --pagination-item-color: var(--neutral-500);

    &-number {
      border: 0 !important;
      background-color: transparent;
    }

    .bk-button {
      @include for-mobile {
        border: 0 !important;
        background-color: transparent;
      }
    }

    &:not(.current) {
      margin-top: 4px;
    }

    &:hover {
      cursor: pointer;
      --pagination-item-color: var(--color-primary) !important;
    }

    &.current {
      --pagination-item-color: var(--neutral-900) !important;
      font-weight: 620;
      border-top: 4px solid var(--primary-500) !important;
      cursor: default;
    }
  }
}
</style>
